// ---------------------------------------------------------------------------
// Testimonial Grid Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface Testimonial {
  quote?: string;
  text?: string;
  name: string;
  role?: string;
  company?: string;
  avatarUrl?: string;
  imageUrl?: string;
  rating?: number;
}

interface TestimonialGridProps {
  heading?: string;
  testimonials: Testimonial[];
  columns?: number;
  showRating?: boolean;
}

function renderStars(rating: number): string {
  const clamped = Math.max(0, Math.min(5, Math.round(rating)));
  const filled = "\u2605".repeat(clamped);
  const empty = "\u2606".repeat(5 - clamped);
  return `<span class="ease-testimonial-rating" aria-label="${attr(String(clamped))} out of 5 stars">${filled}${empty}</span>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as TestimonialGridProps;

  const heading = p.heading ?? "What Our Customers Say";
  const testimonials = Array.isArray(p.testimonials) ? p.testimonials : [];
  const columns = Math.max(1, Math.min(4, p.columns ?? 3));
  const showRating = p.showRating !== false;

  const headingHtml = `<h2 class="ease-testimonials-heading">${text(heading)}</h2>`;

  const itemsHtml = testimonials
    .map((t) => {
      const quoteText = t.quote ?? t.text ?? "";
      const imgSrc = t.avatarUrl ?? t.imageUrl;
      const avatarHtml = imgSrc
        ? `<img src="${url(imgSrc)}" alt="${attr(t.name)}" class="ease-testimonial-avatar" loading="lazy" width="64" height="64">`
        : "";

      const ratingHtml = showRating && t.rating ? `\n      ${renderStars(t.rating)}` : "";

      const roleHtml = t.role
        ? `<span class="ease-testimonial-role">${text(t.role)}</span>`
        : "";
      const companyHtml = t.company
        ? `<span class="ease-testimonial-company">${text(t.company)}</span>`
        : "";
      const metaLine = [roleHtml, companyHtml].filter(Boolean).join(", ");

      return `    <article class="ease-testimonial-card">
      <blockquote class="ease-testimonial-quote">${text(quoteText)}</blockquote>${ratingHtml}
      <footer class="ease-testimonial-author">
        ${avatarHtml}
        <div class="ease-testimonial-info">
          <cite class="ease-testimonial-name">${text(t.name)}</cite>
          ${metaLine ? `<span class="ease-testimonial-meta">${metaLine}</span>` : ""}
        </div>
      </footer>
    </article>`;
    })
    .join("\n");

  return `<section class="ease-section ease-testimonial-grid" aria-label="${attr(heading)}">
  ${headingHtml}
  <div class="ease-testimonials-grid" style="display: grid; grid-template-columns: repeat(${attr(String(columns))}, 1fr); gap: var(--ease-spacing-lg, 2rem);">
${itemsHtml}
  </div>
</section>`;
}

registerRenderer("testimonial-grid", { render });
